import React, { useEffect, useRef, useState } from 'react';

import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { Table, Card, Row, Col, Button } from 'react-bootstrap';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { max_precision, hr_by_day, add_duration, sortByHeightDesc } from './logic';
import DashboardHeader from './DashboardHeader';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { PointIcon } from './PointIcon';
import './dashboard.css';
import { CopyIcon } from './CopyIcon';
import { DownloadIcon} from './DownloadIcon'
import { renderChart } from './chartBuilder';
import { getLastBlock, getPayments, getLastShareInfo } from './api';

const DashboardContainer = styled.div`
width: 100%;
background: #000;
padding: 12px 12px 0 12px;
`
const states = ["DUE", "ERROR", "PAID"];

const WithTooltip = ({ id, children, title }) => (
    <OverlayTrigger overlay={<Tooltip id={id}>{title}</Tooltip>}>
        {children}
    </OverlayTrigger>
);



const paymentRefreshRate = 60 * 1000;
const lastShareRefreshRate = 15 * 1000;

function Dashboard(props) {
    const paymentsInterval = useRef();
    const lastShareInterval = useRef();
    const lastShareSeen = useRef();

    let navigate = useNavigate();

    const [data, setData] = useState();
    const [chartOptions, setChartOptions] = useState(null);
    const [useHighlight, setUseHighlight] = useState(false);

    const [lastBlock, setLastBlock] = useState(0);

    const onDownload = () => {
        let csvContent = "";
        csvContent += Object.keys(data[0]).join(",") + "\r\n";
        data.forEach(function (rowArray) {
            let row = Object.values(rowArray).map(x => x.toString()).join(",");
            csvContent += row + "\r\n";
        });
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.setAttribute('href', url)
        a.setAttribute('download', 'payouts.csv');
        a.click()
    }

    useEffect(() => {
        if (!props.email) {
            navigate("/");
            return;
        }
        clearInterval(paymentsInterval.current);
        clearInterval(lastShareInterval.current);

        const refreshPayments = async () => {
            const lastBlock = await getLastBlock();
            const payments = await getPayments(props.email);
            processResponse(payments);
            setLastBlock(lastBlock)
        }

        const refreshLastShare = async () => {
            const info = await getLastShareInfo(props.email)
            if(!info)
                return;

            const [height, diff, timestamp] = info;

            if(lastShareSeen?.current?.timestamp != timestamp) {
                highlightUpdate();
            }

            lastShareSeen.current = { height, diff, timestamp };
        }

        refreshPayments();
        refreshLastShare();

        paymentsInterval.current = setInterval(refreshPayments, paymentRefreshRate);
        lastShareInterval.current = setInterval(refreshLastShare, lastShareRefreshRate);

        return () => {
            clearInterval(paymentsInterval.current);
            clearInterval(lastShareInterval.current);
        }
    }, [])

    const highlightUpdate = () => {
        const duration = 3000;
        console.log("Share received");
        setUseHighlight(true);
        setTimeout(() => setUseHighlight(false), duration);
    }

    const processResponse = (data) => {
        add_duration(data);
        const agg = hr_by_day(data);
        const chart = renderChart(agg);
        data.sort(sortByHeightDesc);
        const mappedData = data.map(row => ({
            state: states[row[0]],
            block: row[1],
            time: new Date(row[2] * 1000).toISOString().split(".000").join(""),
            diff: row[3],
            duration: row[5],
            hashrate: toHashrate(row[3], row[5]),
            sats: row[4]
        }))
        setData(mappedData);
        setChartOptions(chart)
    }

    const toHashrate = (diff, duration) => max_precision(diff / duration * 2 ** 32  / 1e12, 2);

    const renderLastSeen = (timestamp) => {
        var input = new Date(timestamp * 1000);
        var todaysDate = new Date().setHours(0,0,0,0);
        var inputDate = new Date(input).setHours(0,0,0,0);

        return todaysDate == inputDate ? input.toLocaleString() : input.toLocaleDateString();
    }

    if (!data) {
        return <></>
    }

    const currentHashrate = lastBlock - 1 > data[0]?.block ? 0 : data[0]?.hashrate || 0;

    return (
        <div className='dashboard-container'>
            <DashboardHeader logout={() => {
                props.cleanup();
                navigate("/");
            }} email={props.email} />

            <DashboardContainer>
                <Row>
                    <Col xl={8} l={8}>
                        <Card>
                            <Card.Body style={{ padding: '24px 24px 0 24px' }}>
                                <div className='info-title'>
                                    <PointIcon />
                                    <span className='info-container'>Hashrate</span>
                                </div>
                                <HighchartsReact
                                    highcharts={Highcharts}
                                    options={chartOptions}
                                />
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xl={4} l={4}>
                        <Card>
                            <Card.Body>
                                <div className='info-title'>
                                    <PointIcon />
                                    <span className='info-container'>Current Hashrate</span>
                                </div>

                                <div className='info-container-body hashrate'>
                                    <h2>{currentHashrate} TH/s</h2>
                                </div>

                                {
                                    lastShareSeen?.current && 
                                    <div className={ useHighlight ? 'last-seen-label highlight' : 'last-seen-label' }>
                                        Last Share: {renderLastSeen(lastShareSeen.current.timestamp)} ~ {toHashrate(lastShareSeen.current.diff, 30)} TH/s
                                    </div>
                                }
                            </Card.Body>
                        </Card>
                        <Card>
                            <Card.Body>
                                <div className='info-title'>
                                    <PointIcon />
                                    <span className='info-container'>Get Connected</span>
                                </div>

                                <div className='info-container-body creds' style={{ marginBottom: '8px' }}>
                                    <div className='creds-body'>
                                        <div className='creds-title'>Pool URL</div>
                                        <div className='creds-value'>stratum+tcp://pplp.titan.io:4141</div>
                                    </div>

                                    <WithTooltip title="Copy to clipboard">
                                        <Button
                                            onClick={() => {
                                                navigator.clipboard.writeText('stratum+tcp://pplp.titan.io:4141')
                                            }}
                                            style={{
                                                backgroundColor: 'rgba(52, 82, 244, 0.2)',
                                                height: '38px',
                                                width: '48px',
                                                cursor: "pointer",
                                                marginLeft: '15px',
                                                border: 'none'
                                            }}>
                                            <CopyIcon
                                            ></CopyIcon></Button>

                                    </WithTooltip>
                                </div>

                                <div className='info-container-body creds'>
                                    <div className='creds-body'>
                                        <div className='creds-title'>Username</div>
                                        <div className='creds-value'>{props.email}</div>
                                    </div>

                                    <WithTooltip title="Copy to clipboard">
                                        <Button
                                            onClick={() => {
                                                navigator.clipboard.writeText(props.email)
                                            }}
                                            style={{
                                                backgroundColor: 'rgba(52, 82, 244, 0.2)',
                                                height: '38px',
                                                width: '48px',
                                                cursor: "pointer",
                                                marginLeft: '15px',
                                                border: 'none'
                                            }}>
                                            <CopyIcon
                                            ></CopyIcon></Button>
                                    </WithTooltip>
                                </div>

                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                <Row>
                    <Col xl={8} l={8}>
                        <Card style={{ padding: '24px' }}>
                            <Card.Title>
                                <div className='payouts-title'>
                                <div className='info-title'>
                                    <PointIcon />
                                    <span className='info-container'>Payouts</span>
                                </div>
                                <div>
                                    <WithTooltip title="Download CSV">
                                    <Button
                                            onClick={() => {
                                                onDownload()
                                            }}
                                            className='download'>
                                                <span>Download CSV</span>
                                                <DownloadIcon></DownloadIcon>
                                            </Button>
                                    </WithTooltip>
                                </div>
                                </div>
                            </Card.Title>
                            <Card.Body style={{ maxHeight: '300px', padding: 0, overflowY: 'auto' }}>
                                <Table>
                                    <thead className='payouts-header'>
                                        <tr>
                                            <th>Status</th>
                                            <th>Block</th>
                                            <th>Time</th>
                                            <th className='d-none d-lg-table-cell d-xl-table-cell'>Diff</th>
                                            <th className='d-none d-lg-table-cell d-xl-table-cell'>Duration</th>
                                            <th>TH/s</th>
                                            <th>Sats</th>
                                        </tr>
                                    </thead>
                                    <tbody className='payouts-body'>
                                        {data?.map(row => (
                                            <tr key={row.block}>
                                                <td>{row.state}</td>
                                                <td>{row.block}</td>
                                                <td>{new Date(row.time).toLocaleString()}</td>
                                                <td className='d-none d-lg-table-cell d-xl-table-cell'>{row.diff}</td>
                                                <td className='d-none d-lg-table-cell d-xl-table-cell'>{row.duration}</td>
                                                <td>{row.hashrate}</td>
                                                <td>{row.sats}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

            </DashboardContainer>
        </div>
    );
}

export default Dashboard;
